import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions, loadStripe } from '@stripe/stripe-js';

import { selectAppLocale } from 'features/locale/localeSelectors';

import LOCALES from 'constants/locales';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface StripeProviderProps {
  children: React.ReactNode;
}

// Stripe doesn't support Hindi, Ukrainian, Azerbaijani - use English for them
const STRIPE_LOCALE_MAP: Partial<
  Record<string, StripeElementsOptions['locale']>
> = {
  [LOCALES.ENGLISH_US]: 'en',
  [LOCALES.SPANISH_ES]: 'es',
  [LOCALES.SPANISH_LATAM]: 'es',
  [LOCALES.PORTUGUESE_BR]: 'pt',
  [LOCALES.PORTUGUESE_PT]: 'pt',
  [LOCALES.FRENCH_FR]: 'fr',
  [LOCALES.RUSSIAN_RU]: 'ru',
  [LOCALES.GERMAN_DE]: 'de',
  [LOCALES.TURKISH_TR]: 'tr',
  [LOCALES.ROMANIAN_RO]: 'ro',
  [LOCALES.ITALIAN_IT]: 'it'
};

export const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const appLocale = useSelector(selectAppLocale);

  const config: StripeElementsOptions = useMemo(
    () => ({
      locale: STRIPE_LOCALE_MAP[appLocale] ?? 'en',
      loader: 'never'
    }),
    [appLocale]
  );

  return (
    <Elements stripe={stripePromise} options={config}>
      {children}
    </Elements>
  );
};
